import React, { useContext, useState } from "react";
import { SettingsContext } from "../../contexts";
import { Separator } from "../index.jsx";
import { Button, Checkbox, Icon, SplitButton, Toggle } from "../ui";
import { ListingProductDetails } from "./ListingProductDetails";
import { Visible } from "../../core/components";
import { ListingActionButton } from "./ListingActionButton";
import { PriceWarningModal } from "../modals";
import { useListingDetailsRow } from "./useListingDetailsRow";

// Two states for a four-value field. It says ON only for "owned"; for the two
// values it cannot express ("exclusive", "to_be_procured") it reads OFF, is
// disabled, and names the real value — clicking it would otherwise overwrite
// Owned By with "owned"/"no". See internalProductLocked in useListingDetailsRow.
const INTERNAL_PRODUCT_LABELS = {
  exclusive: "Consignment",
  to_be_procured: "To be procured",
};

// Toggle treats a returned promise as "save in flight" and shows the new state
// optimistically until `checked` changes. These handlers own their state (and
// some only open the price-warning dialog), so they are called fire-and-forget,
// exactly as the old react-switch called them.
const fire = (handler) => (val) => {
  handler(val);
};

function InternalProductSwitch({
  internalProduct,
  onInternalProductChange,
  internalProductLocked,
  settings,
}) {
  const lockedLabel = INTERNAL_PRODUCT_LABELS[internalProduct];
  return (
    <Toggle
      size="sm"
      tone="active"
      labelPosition="start"
      label={
        internalProductLocked && lockedLabel
          ? lockedLabel
          : settings.acronym || settings.name
      }
      data-btn="listing-owned"
      onChange={fire(onInternalProductChange)}
      checked={internalProduct === "owned"}
      disabled={!!internalProductLocked}
    />
  );
}

// Likes / views — read-only counts, not buttons.
function Stats({ listing }) {
  return (
    <>
      <span className="d-inline-flex align-items-center gap-1 me-3">
        <Icon name="Star" size="sm" label="Likes" />
        {listing.likeCount}
      </span>
      <span className="d-inline-flex align-items-center gap-1 me-2">
        <Icon name="Eye" size="sm" label="Views" />
        {listing.viewCount}
      </span>
    </>
  );
}

// Network rows: Import with the same Importing… / Imported feedback the old
// FeedbackButton gave, on the style-guide Button.
function ImportButton({ label, onClick, submitting, success, failure = "Failed!" }) {
  const [text, setText] = useState(label);
  const onImport = async () => {
    setText(submitting);
    try {
      await onClick();
      setText(success);
    } catch (err) {
      setText(failure);
      throw err;
    } finally {
      setTimeout(() => setText(label), 1000);
    }
  };
  return (
    <Button variant="secondary" size="sm" data-btn="listing-import" onClick={() => onImport()}>
      {text}
    </Button>
  );
}

const DetailsListingRow = ({
  listing,
  isNetwork,
  importListing,
  assignListing,
  isSelectListing = false,
}) => {
  const row = useListingDetailsRow({
    listing,
    isNetwork,
    importListing,
    assignListing,
    isSelectListing,
  });

  const { isShopify } = useContext(SettingsContext);

  if (row.deleted) {
    return null;
  }

  const {
    product,
    settings,
    reserved,
    chrono,
    ebay,
    internalProduct,
    watchFinder,
    showPriceWarningModal,
    pendingToggleType,
    isExpanded,
    skipCompCheck,
    shouldShowCheckbox,
    handleRowClick,
    handleCompCheckboxClick,
    onSyncChronoChange,
    onSyncEbayChange,
    onReserveChange,
    onInternalProductChange,
    internalProductLocked,
    onSyncWatchFinderChange,
    activeSwitchProps,
    inStockSwitchProps,
    editButtonProps,
    importButtonProps,
    handleModalEdit,
    handleModalConfirm,
    closeModal,
  } = row;

  const notActive = listing.status !== "active";

  const activeToggle = (label) => (
    <Toggle
      size="sm"
      tone="active"
      labelPosition="start"
      label={label}
      data-btn="listing-active"
      checked={activeSwitchProps.checked}
      onChange={fire(activeSwitchProps.onChange)}
    />
  );

  const inStockToggle = (
    <Toggle
      size="sm"
      labelPosition="start"
      label="In stock"
      data-btn="listing-in-stock"
      checked={inStockSwitchProps.checked}
      onChange={fire(inStockSwitchProps.onChange)}
    />
  );

  const reservedToggle = (
    <Toggle
      size="sm"
      labelPosition="start"
      label="Reserved"
      data-btn="listing-reserve"
      onChange={fire((val) => onReserveChange(val))}
      checked={!!reserved}
    />
  );

  const ebayToggle = (
    <Toggle
      size="sm"
      labelPosition="start"
      label="eBay"
      data-btn="listing-ebay"
      onChange={fire(onSyncEbayChange)}
      checked={!!ebay}
      disabled={notActive}
    />
  );

  const chronoToggle = (
    <Toggle
      size="sm"
      labelPosition="start"
      label="Chrono24"
      data-btn="listing-chrono"
      onChange={fire(onSyncChronoChange)}
      checked={!!chrono}
      disabled={notActive}
    />
  );

  const watchfinderToggle = (
    <Toggle
      size="sm"
      labelPosition="start"
      label="Watchfinder"
      data-btn="listing-watchfinder"
      onChange={fire(onSyncWatchFinderChange)}
      checked={!!watchFinder}
      disabled={notActive || !watchFinder}
    />
  );

  const editButton = (
    <SplitButton
      variant="secondary"
      size="sm"
      data-btn="listing-edit"
      onClick={editButtonProps.onClick}
      items={editButtonProps.options.map((o) => ({
        label: o.label,
        onSelect: o.onClick,
        // The reference row is information only, as before.
        disabled: !o.onClick,
        danger: o.label === "Delete",
      }))}
    >
      {editButtonProps.label}
    </SplitButton>
  );

  const assignButton = (
    <Button
      variant="secondary"
      size="sm"
      data-btn="listing-assign"
      onClick={() => assignListing(listing)}
    >
      Assign
    </Button>
  );

  const compCheckbox = (platform, label) => (
    <div className="comp-checkbox-item">
      <Checkbox
        label={label}
        checked={!!skipCompCheck[platform]}
        onChange={(_checked, e) => handleCompCheckboxClick(platform, e)}
      />
    </div>
  );

  return (
    <>
      <div
        className="listing-details-row col-12 p-2 p-lg-4 ps-lg-5"
        onClick={handleRowClick}
        style={{ cursor: "pointer" }}
      >
        <div className="d-lg-flex justify-content-between">
          <div className="d-flex justify-content-between d-lg-none">
            <div className="stats">
              <Visible when={!isNetwork && !isShopify}>
                <Stats listing={listing} />
              </Visible>
            </div>

            <div className="d-flex justify-content-end active-switch-container">
              {activeToggle(listing.status === "active" ? "Active" : "Inactive")}
            </div>
            <Visible when={isNetwork}>
              <div className="d-flex justify-content-end active-switch-container">
                {inStockToggle}
              </div>
            </Visible>
          </div>

          <div className="d-lg-none">
            <Separator noMargin className="mt-2 mb-2" />
          </div>

          <ListingProductDetails
            listing={listing}
            product={product}
            isNetwork={isNetwork}
          />

          <div className="d-lg-none">
            <Separator noMargin className="mt-2 mb-2" />
          </div>

          <div className="d-none flex-column justify-content-between switch-container pe-4 d-lg-flex">
            <Visible when={!isNetwork && !isShopify}>
              <div className="switches mt-2 d-flex flex-column gap-2">
                <div className="d-flex justify-content-between gap-3 switch">
                  <InternalProductSwitch
                    internalProduct={internalProduct}
                    onInternalProductChange={onInternalProductChange}
                    internalProductLocked={internalProductLocked}
                    settings={settings}
                  />
                  {activeToggle("On")}
                </div>
                <div className="d-flex justify-content-between gap-3 switch">
                  {reservedToggle}
                  {ebayToggle}
                </div>
                <div className="switch">{chronoToggle}</div>
                <div className="switch">{watchfinderToggle}</div>
              </div>
            </Visible>
          </div>

          <div className="ml-5 d-none d-lg-flex flex-column justify-content-between">
            <div className="d-flex justify-content-between align-items-center active-switch-container mb-3">
              <div className="stats d-flex">
                <Stats listing={listing} />
              </div>
            </div>
            <div className="d-flex justify-content-end active-switch-container">
              <Visible when={isNetwork}>{inStockToggle}</Visible>
            </div>
            <div className="d-flex justify-content-start justify-content-lg-end mb-2">
              {!isNetwork && !isSelectListing && editButton}
              {isNetwork && !isSelectListing && (
                <ImportButton {...importButtonProps} />
              )}
              {isSelectListing && assignButton}
            </div>
            {!isNetwork && !isSelectListing && (
              <div className="d-flex justify-content-start justify-content-lg-end">
                <ListingActionButton listing={listing} />
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between d-lg-none">
            <div className="switches d-flex flex-column gap-2">
              <Visible when={!isNetwork && !isShopify}>
                <div className="switch">
                  <InternalProductSwitch
                    internalProduct={internalProduct}
                    onInternalProductChange={onInternalProductChange}
                    internalProductLocked={internalProductLocked}
                    settings={settings}
                  />
                </div>
                <div className="switch">{reservedToggle}</div>
                <div className="switch">{ebayToggle}</div>
                <div className="switch">{chronoToggle}</div>
                <div className="switch">{watchfinderToggle}</div>
              </Visible>
            </div>

            <div className="d-flex flex-column justify-content-end gap-2">
              {!isNetwork && !isSelectListing && editButton}
              {isNetwork && !isSelectListing && (
                <ImportButton {...importButtonProps} />
              )}
              {!isNetwork && !isSelectListing && (
                <ListingActionButton listing={listing} />
              )}
              {isSelectListing && assignButton}
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="comp-checkboxes">
          <div className="comp-checkboxes-container d-flex flex-row mt-0">
            {!shouldShowCheckbox("ebay") &&
              !shouldShowCheckbox("chrono") &&
              !shouldShowCheckbox("wf") && (
                <div className="no-checkboxes-message w-100">
                  <small className="text-white">&nbsp;</small>
                </div>
              )}

            {shouldShowCheckbox("ebay") && compCheckbox("ebay", "eBay")}

            {(shouldShowCheckbox("chrono") || shouldShowCheckbox("wf")) && (
              <div className="comp-checkbox-group">
                {shouldShowCheckbox("chrono") && compCheckbox("chrono", "Chrono24")}
                {shouldShowCheckbox("wf") && compCheckbox("wf", "Watchfinder")}
              </div>
            )}
          </div>
        </div>
      )}

      <PriceWarningModal
        open={showPriceWarningModal}
        closeModal={closeModal}
        type={pendingToggleType}
        onEdit={handleModalEdit}
        onConfirm={handleModalConfirm}
      />
    </>
  );
};

export default DetailsListingRow;
